import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MessageCircle, MapPin } from "lucide-react";
import { HERO_IMAGE, FALLBACK_IMG, scrollToId, waLink, DEFAULT_WA_MESSAGE } from "@/lib/site";

const LINES = ["Furniture That Makes", "Your Space Feel", "Like Home"];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section id="home" ref={ref} className="relative overflow-hidden">
      <div className="section-shell grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-6 pt-10 pb-16 sm:pt-16 sm:pb-24 items-center">
        <motion.div style={{ y: textY }} className="lg:col-span-6 relative z-10">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="eyebrow flex items-center gap-2"
            data-testid="hero-eyebrow"
          >
            <span className="inline-block h-px w-8 bg-brass" /> 01 / Atelier — Mumbai · Navi Mumbai · Thane
          </motion.p>

          <h1
            data-testid="hero-main-headline"
            className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl font-normal tracking-tight text-walnut leading-[1.08]"
          >
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${i === 2 ? "italic text-teak" : ""}`}
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.25 + i * 0.14, ease: [0.215, 0.61, 0.355, 1] }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            data-testid="hero-subheading-text"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75 }}
            className="mt-6 max-w-md text-base sm:text-lg leading-relaxed text-stone-600"
          >
            Premium wooden furniture designed for modern homes — handcrafted in our
            Mumbai workshops and delivered to your doorstep.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.9 }}
            className="mt-9 flex flex-wrap items-center gap-5"
          >
            <button
              data-testid="hero-cta-shop-now"
              onClick={() => scrollToId("showcase")}
              className="group inline-flex items-center gap-3 rounded-full bg-walnut px-8 py-4 text-sm font-semibold text-sand transition-all duration-300 hover:bg-noir hover:shadow-[0_18px_40px_-14px_rgba(46,28,20,0.5)]"
            >
              Shop Now
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <a
              data-testid="hero-whatsapp-text-link"
              href={waLink(DEFAULT_WA_MESSAGE)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm font-medium text-stone-600 hover:text-[#1EBE5D] transition-colors"
            >
              <MessageCircle size={16} className="text-[#1EBE5D]" />
              WhatsApp us — replies within the hour
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.1 }}
            className="mt-10 flex items-center gap-2 text-xs font-mono uppercase tracking-[0.18em] text-stone-500"
          >
            <MapPin size={13} className="text-brass" />
            Factory-direct wooden furniture across the Mumbai MMR
          </motion.div>
        </motion.div>

        <div className="lg:col-span-6 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.4, ease: [0.215, 0.61, 0.355, 1] }}
            className="relative"
          >
            <div className="absolute -top-4 -right-4 h-full w-full border border-brass/50 rounded-t-[10rem] pointer-events-none" />
            <div className="img-frame rounded-t-[10rem] aspect-[4/5] max-h-[80vh] w-full relative overflow-hidden">
              <motion.img
                style={{ y: imgY, scale: 1.15 }}
                src={HERO_IMAGE}
                onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }}
                alt="Premium wooden furniture in a modern Mumbai living room"
                className="h-full w-full object-cover"
                data-testid="hero-image"
              />
            </div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1.2 }}
              data-testid="hero-timber-guarantee-badge"
              className="absolute -bottom-6 -left-4 sm:-left-8 rounded-2xl border border-[#E7DFD5] bg-white/95 backdrop-blur px-5 py-4 shadow-[0_20px_50px_-20px_rgba(46,28,20,0.35)]"
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-teak">Our Promise</p>
              <p className="mt-1 font-serif text-base sm:text-lg text-walnut">100% Solid Seasoned Wood</p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
